import { FloatingLoader, Typography } from "@/components";
import useCases from "@/hooks/useCases";
import { createFileRoute } from "@tanstack/react-router";
import CaseCard from "../-components/CaseCard";
import EmptyCase from "../-components/Empty";

export const Route = createFileRoute("/dashboard/case/progress-notes/")({
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = Route.useNavigate();
	const {
		scrollRef,
		caseItems,
		isEmpty,
		isEmptySearch,
		searchQ,
		casesVirtualizer,
		casesVirtualizerItems,
		isLoading,
		isFetchingNextPage,
	} = useCases({
		generateList: (query, items) => {
			const result: ReturnType<
				NonNullable<NonNullable<Parameters<typeof useCases>[0]>["generateList"]>
			> = [];

			const filteredItems = query.trim()
				? items.filter(
						(item) =>
							item.title?.toLowerCase()?.includes(query.toLowerCase()) ||
							item.summary?.toLowerCase()?.includes(query.toLowerCase()) ||
							item.patient?.name
								?.toLowerCase()
								?.includes(query.toLowerCase()) ||
							item.patient?.surname
								?.toLowerCase()
								?.includes(query.toLowerCase()),
					)
				: items;

			if (filteredItems.length === 0) {
				return result;
			}

			const originalTop3 = items.slice(0, 3);
			const originalRemaining = items.slice(3);

			const filteredRecentItems = filteredItems.filter((item) =>
				originalTop3.some((originalItem) => originalItem.id === item.id),
			);

			const filteredAllItems = filteredItems.filter((item) =>
				originalRemaining.some((originalItem) => originalItem.id === item.id),
			);

			if (filteredRecentItems.length > 0) {
				result.push({
					type: "header",
					label: "Recent Cases",
					id: "header-recent",
				});
				for (const item of filteredRecentItems) {
					result.push({ type: "item", data: item, id: `recent-${item.id}` });
				}
			}

			if (filteredAllItems.length > 0) {
				result.push({
					type: "header",
					label: "All Active Cases",
					id: "header-all",
				});
				for (const item of filteredAllItems) {
					result.push({ type: "item", data: item, id: `all-${item.id}` });
				}
			}

			return result;
		},
	});

	return (
		<div ref={scrollRef} className="relative h-full w-full overflow-y-auto">
			<FloatingLoader open={isLoading || isFetchingNextPage} />
			<div
				className="relative w-full"
				style={{
					height: casesVirtualizer.getTotalSize(),
				}}
			>
				{casesVirtualizerItems.map((casesVirtualizerItem) => {
					const caseItem = caseItems[casesVirtualizerItem.index];
					return (
						<div
							key={casesVirtualizerItem.key}
							data-index={casesVirtualizerItem.index}
							ref={casesVirtualizer.measureElement}
							className="container absolute top-0 left-0"
							style={{
								transform: `translateY(${casesVirtualizerItem.start}px)`,
							}}
						>
							{"type" in caseItem && caseItem.type === "item" ? (
								// biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
								<div
									// biome-ignore lint/a11y/useSemanticElements: <explanation>
									role="link"
									tabIndex={0}
									onClick={() =>
										navigate({
											to: "/dashboard/case/view/$id/detail",
											params: { id: caseItem.data.id },
											search: { tab: "medical_notes" },
										})
									}
									className="cursor-pointer"
								>
									<CaseCard
										type="item"
										caseItem={caseItem.data}
										showLastMessage={false}
									/>
								</div>
							) : (
								<Typography fontWeight={900} fontSize={14}>
									{"label" in caseItem && caseItem.label}
								</Typography>
							)}
						</div>
					);
				})}
			</div>
			{isEmpty ? <EmptyCase type="list" title="No cases added yet" /> : null}
			{isEmptySearch ? (
				<EmptyCase type="list" title={`No results found for '${searchQ}'`} />
			) : null}
		</div>
	);
}
